<template>
  <main v-if="status">
    <loading :status="status" />
  </main>
  <main v-else>
    <h2>My Projects</h2>

    <div class="filter-section">
      <div class="filter-row">
        <input
          type="text"
          v-model="searchText"
          placeholder="Search in name or description..."
          class="search-input"
        />
      </div>

      <div class="filter-row" v-if="allSkills.length > 0">
        <div class="skills-filter-header">
          <label class="filter-label">Filter by skills:</label>
          <select v-model="skillFilterMode" class="skill-mode-select">
            <option value="any">ANY (at least one)</option>
            <option value="all">ALL (must have all)</option>
          </select>
        </div>
        <div class="skill-filters">
          <label
            v-for="skill in allSkills"
            :key="skill.id"
            :style="{
              backgroundColor: skill.color,
              color: getTextColor(skill.color)
            }"
            class="skill-checkbox"
            :class="{ active: selectedSkillIds.includes(skill.id) }"
          >
            <input
              type="checkbox"
              :value="skill.id"
              v-model="selectedSkillIds"
            />
            {{ skill.name }} ({{ skill.weight }})
          </label>
        </div>
      </div>

      <div class="filter-row">
        <label class="filter-label">Filter by status:</label>
        <div class="workflow-filters">
          <label class="workflow-option" :class="{ active: workflowStatus === 'all' }">
            <input type="radio" value="all" v-model="workflowStatus" />
            All
          </label>
          <label class="workflow-option" :class="{ active: workflowStatus === 'draft' }">
            <input type="radio" value="draft" v-model="workflowStatus" />
            Draft
          </label>
          <label class="workflow-option" :class="{ active: workflowStatus === 'requested' }">
            <input type="radio" value="requested" v-model="workflowStatus" />
            Requested
          </label>
          <label class="workflow-option" :class="{ active: workflowStatus === 'confirmed' }">
            <input type="radio" value="confirmed" v-model="workflowStatus" />
            Confirmed
          </label>
          <label class="workflow-option" :class="{ active: workflowStatus === 'done' }">
            <input type="radio" value="done" v-model="workflowStatus" />
            Done
          </label>
        </div>
      </div>

      <div class="filter-results" v-if="searchText || selectedSkillIds.length > 0 || workflowStatus !== 'all'">
        Found {{ filteredProjects.length }} project(s)
      </div>
    </div>

    <div v-for="project in filteredProjects" :key="project.id" class="project-container">
      <h3 class="main">{{ project.name }}</h3>
      <div class="project">
        <div class="project-description markdown-content" v-html="markdown(project.description)"></div>
        <div class="project-attributes">
          <p>
            <span :class="{ 'highlight': project.request_date && !project.confirm_date }">Request:</span><br />
            <span v-if="project.request_date">{{ project.request_date }}</span>
            <span v-else>...</span>
          </p>
          <p>
            <span :class="{ 'highlight': project.confirm_date && !project.done_date }">Confirm:</span><br />
            <span v-if="project.confirm_date">{{ project.confirm_date }}</span>
            <span v-else>...</span>
          </p>
          <p>
            <span :class="{ 'highlight': project.done_date }">Done:</span><br />
            <span v-if="project.done_date">{{ project.done_date }}</span>
            <span v-else>...</span>
          </p>
          <p class="video" v-if="project.video_required">🎬</p>
          <dl>
            <dt>Skills:</dt>
            <dd v-for="skill in project.skills" :key="skill.id" :style="{ backgroundColor: skill.color }">
              <RouterLink :to="{ name: 'skill-detail', params: { id: skill.id } }"
                :style="{ color: getTextColor(skill.color) }">
                {{ skill.name }}({{ skill.weight }})
              </RouterLink>
            </dd>
          </dl>
          <RouterLink :to="{ name: 'project-detail', params: { id: project.id } }" class="btn">
            Details
          </RouterLink>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped lang="scss">

.highlight {
  font-weight: bold;
  color: $primari;
}

h3.main {
  margin-left: 1rem;
  text-decoration: underline;
  font-size: 2rem;
  color: $primari;
}

.filter-section {
  margin: 0 0 2rem 0;
  padding: 1rem;
  background-color: rgba($primari, 0.05);
  border-radius: 8px;
  border: 1px solid rgba($primari, 0.2);
}

.filter-row {
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.search-input {
  width: 100%;
  padding: 0.7rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: $primari;
    box-shadow: 0 0 0 2px rgba($primari, 0.1);
  }
}

.filter-label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #333;
}

.skills-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.skill-mode-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: $primari;
  }

  &:hover {
    border-color: $primari;
  }
}

.skill-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.skill-checkbox {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: normal;
  opacity: 0.7;
  border: 2px solid transparent;

  &.active {
    opacity: 1;
    border: 2px solid rgba(0, 0, 0, 0.3);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  input[type="checkbox"] {
    margin-right: 0.4rem;
    cursor: pointer;
  }
}

.workflow-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
}

.workflow-option {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.7rem;
  border-radius: 4px;
  cursor: pointer;
  background-color: #f5f5f5;
  border: 2px solid #ddd;
  transition: all 0.2s ease;
  font-weight: normal;

  &.active {
    background-color: $primari;
    color: white;
    border-color: $primari;
    font-weight: bold;
  }

  &:hover {
    border-color: $primari;
    transform: translateY(-1px);
  }

  input[type="radio"] {
    margin-right: 0.5rem;
    cursor: pointer;
  }
}

.filter-results {
  margin-top: 1rem;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
}

.project-container {
  border: 1px solid #ddd;
  margin: 1rem auto;

  &:hover {
    box-shadow: 0px 0px 7px 7px $primari;
  }
}

.project {
  display: flex;
}

.project-description {
  flex: 10;
  border: 2px solid #ccc;
  max-height: 33rem;
  margin: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  overflow: auto;
}

.project-attributes {
  flex: 2;
  //max-height: 30rem;
  margin-bottom: 1rem;
  overflow: auto;

  p {
    margin: 0 0 1rem 0;

    :first-child {
      font-weight: bold;
    }
  }

  dt {
    font-weight: bold;
  }

  dd {
    display: inline-block;
    margin: 0.3em;
    border-radius: 1ex;

    a {
      display: inline-block;
      padding: 0.5em;
    }
  }
}

.btn {
  margin-top: 1rem;
  display: inline-block;
  padding: 12px 24px;
  background-color: $primari;
  font-weight: bold;
  color: white;
  text-decoration: none;
  font-size: 16px;
  border-radius: 5px;
  transition: background 0.3s ease;
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { api } from '../utils/axios'
import { markdown, getTextColor } from '../utils/helpers'
import loading from '@/components/loading.vue'

const projects = ref([])
const status = ref('loading')

// Filtrační proměnné
const searchText = ref('')
const selectedSkillIds = ref([])
const skillFilterMode = ref('any')  // 'all' nebo 'any'
const workflowStatus = ref('all')

// Computed: Všechny unikátní skills ze všech projektů
const allSkills = computed(() => {
  const skillsMap = new Map()
  projects.value.forEach(project => {
    project.skills.forEach(skill => {
      if (!skillsMap.has(skill.id)) {
        skillsMap.set(skill.id, skill)
      }
    })
  })
  return Array.from(skillsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'cs')
  )
})

// Computed: Filtrované projekty
const filteredProjects = computed(() => {
  return projects.value.filter(project => {
    // Textový filtr - hledá v názvu a popisu
    const textMatch = searchText.value === '' ||
      project.name.toLowerCase().includes(searchText.value.toLowerCase()) ||
      project.description.toLowerCase().includes(searchText.value.toLowerCase())

    // Skill filtr
    let skillMatch = true
    if (selectedSkillIds.value.length > 0) {
      if (skillFilterMode.value === 'all') {
        // AND logika - projekt musí obsahovat VŠECHNY vybrané skills
        skillMatch = selectedSkillIds.value.every(selectedId =>
          project.skills.some(skill => skill.id === selectedId)
        )
      } else {
        // OR logika - projekt musí obsahovat ALESPOŇ JEDEN vybraný skill
        skillMatch = project.skills.some(skill =>
          selectedSkillIds.value.includes(skill.id)
        )
      }
    }

    // Workflow filtr
    let workflowMatch = true
    if (workflowStatus.value !== 'all') {
      switch (workflowStatus.value) {
        case 'draft':
          workflowMatch = !project.request_date
          break
        case 'requested':
          workflowMatch = project.request_date && !project.confirm_date
          break
        case 'confirmed':
          workflowMatch = project.confirm_date && !project.done_date
          break
        case 'done':
          workflowMatch = !!project.done_date
          break
      }
    }

    return textMatch && skillMatch && workflowMatch
  })
})

onMounted(async () => {
  try {
    const response = await api.get('/projects')
    projects.value = response.data
    status.value = null
  } catch (error) {
    status.value = error.response?.data?.msg || 'Error fetching projects data'
  }
})
</script>
